import { getConfig } from '@/config';
import { entraAuthService } from '../auth/entra-auth-service';
import { sharePointAuthService } from '../sharepoint-auth/sharepoint-auth-service';

/**
 * The delegated scope the mail service needs, exported so every caller — including the up-front
 * acquisition inside the click gesture — asks for exactly the same array. MSAL caches per scope set, so
 * a mismatch would mean the consent popup firing again on the first real call.
 */
export const MAIL_SCOPES = ['https://graph.microsoft.com/Mail.ReadWrite'];

/**
 * DEV-ONLY ESCAPE HATCH: acquire the mail token from the SHAREPOINT tenant instead of the app-login one.
 *
 * Exists purely so the feature can be exercised end to end in dev. The dev CESG-login tenant has no
 * Exchange mailbox behind its identities, which surfaces as an empty-bodied 401 from
 * `POST /me/messages` — a valid, correctly-scoped token that Exchange still refuses because there is no
 * mailbox to authenticate into. The SharePoint tenant is a real, licensed tenant, so its identities do
 * have mailboxes.
 *
 * Two independent gates, and BOTH must hold:
 *
 *  1. `import.meta.env.DEV` — false in any production build, whatever the env var says. This is the
 *     load-bearing one: Vite sets it from the build mode rather than from a hand-maintained variable, so
 *     unlike a `VITE_NODE_ENV` check it cannot be got wrong by a deployment config.
 *  2. `VITE_MAIL_FROM_SHAREPOINT_TENANT === 'true'` — opt-in, so a checked-in `.env` is inert by default.
 *
 * Belt and braces on purpose, because the failure mode is nasty and silent: in prod the two tenants are
 * the SAME tenant and the same app registration, so a leaked toggle would keep working and nobody would
 * notice it was reading the wrong instance — precisely the "broken in dev, accidentally correct in prod"
 * trap D1 chose entraAuthService to avoid. It also warns on every acquisition, so an enabled toggle can
 * never be silent.
 *
 * When it is on, drafts land in the mailbox of whoever is signed into the SHAREPOINT tenant, not the CESG
 * identity. That is the point, and it is also why this must never ship: `/me` means a different person.
 *
 * Delete this function, the config key and the `.env` line together when the dev tenant gets a licensed
 * mailbox — nothing else depends on it.
 */
const useSharePointTenantForMail = (): boolean =>
  import.meta.env.DEV &&
  getConfig().MAIL_FROM_SHAREPOINT_TENANT === 'true';

/**
 * The single seam every mail-token acquisition goes through: the four graphMailService operations and the
 * up-front acquisition in useFileManagerMailDraft. One function so the override cannot apply to some of
 * them and not others — a draft created against one tenant and attached against another would fail in a
 * thoroughly confusing way.
 */
export async function acquireMailToken(): Promise<string> {
  if (useSharePointTenantForMail()) {
    console.warn(
      '[dev] VITE_MAIL_FROM_SHAREPOINT_TENANT is on: Outlook drafts are being created in the SharePoint ' +
        `tenant's mailbox (${getConfig().SHAREPOINT_TENANT_ID}), NOT the signed-in CESG identity's. ` +
        'Never enable this outside local development.',
    );
    return sharePointAuthService.acquireToken(MAIL_SCOPES);
  }

  // The production path. Throwing rather than null-returning is the whole reason acquireTokenOrThrow
  // exists: a null here would silently become an unauthenticated Graph call.
  return entraAuthService.acquireTokenOrThrow(MAIL_SCOPES);
}
